// Local storage service for managing keys when users are not logged in
class LocalStorageService {
  static KEYS_STORAGE_KEY = 'securepaper_encryption_keys';

  // Key management methods
  static getKeys() {
    try {
      const keys = localStorage.getItem(this.KEYS_STORAGE_KEY);
      return keys ? JSON.parse(keys) : [];
    } catch (error) {
      console.error('Error retrieving keys from localStorage:', error);
      return [];
    }
  }

  static saveKey({ encryptionKey, name, subject }) {
    try {
      const keys = this.getKeys();
      const keyId = this.generateId();
      const now = new Date().toISOString();
      
      const newKey = {
        id: keyId,
        encryption_key: encryptionKey,
        name: name || `Key for ${subject || 'Untitled'}`,
        created_at: now,
        subject: subject,
        isLocal: true // Flag to identify local storage keys
      };

      keys.push(newKey);
      localStorage.setItem(this.KEYS_STORAGE_KEY, JSON.stringify(keys));
      
      return newKey;
    } catch (error) {
      console.error('Error saving key to localStorage:', error);
      throw new Error('Failed to save key locally');
    }
  }

  static deleteKey(id) {
    try {
      const keys = this.getKeys();
      const filteredKeys = keys.filter(key => key.id !== id);
      localStorage.setItem(this.KEYS_STORAGE_KEY, JSON.stringify(filteredKeys));
      return true;
    } catch (error) {
      console.error('Error deleting key from localStorage:', error);
      throw new Error('Failed to delete key');
    }
  }

  static updateKey({ id, name }) {
    try {
      const keys = this.getKeys();
      const keyIndex = keys.findIndex(key => key.id === id);
      
      if (keyIndex === -1) {
        throw new Error('Key not found');
      }

      keys[keyIndex] = { ...keys[keyIndex], name };
      localStorage.setItem(this.KEYS_STORAGE_KEY, JSON.stringify(keys));
      
      return keys[keyIndex];
    } catch (error) {
      console.error('Error updating key in localStorage:', error);
      throw new Error('Failed to update key');
    }
  }

  static getKeyById(id) {
    try {
      const keys = this.getKeys();
      return keys.find(key => key.id === id) || null;
    } catch (error) {
      console.error('Error retrieving key by ID:', error);
      return null;
    }
  }

  // Sync methods for when user logs in
  static getAllLocalData() {
    return {
      keys: this.getKeys()
    };
  }

  static clearAllLocalData() {
    try {
      localStorage.removeItem(this.KEYS_STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('Error clearing local data:', error);
      return false;
    }
  }

  // Utility methods
  static generateId() {
    // Generate a simple unique ID using timestamp and random number
    return `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  static hasLocalData() {
    const keys = this.getKeys();
    return keys.length > 0;
  }

  // Migration method to sync local data to Supabase when user logs in
  static async syncToSupabase(keyService) {
    const localData = this.getAllLocalData();
    const syncResults = {
      syncedKeys: [],
      errors: []
    };

    try {
      // Sync keys
      for (const localKey of localData.keys) {
        try {
          const syncedKey = await keyService.createKey({
            encryptionKey: localKey.encryption_key,
            name: localKey.name
          });
          syncResults.syncedKeys.push({
            localId: localKey.id,
            remoteKey: syncedKey
          });
        } catch (error) {
          syncResults.errors.push({
            type: 'key',
            localId: localKey.id,
            error: error.message
          });
        }
      }

      // If sync was successful and no critical errors, clear local data
      if (syncResults.errors.length === 0) {
        this.clearAllLocalData();
      }

      return syncResults;
    } catch (error) {
      console.error('Error during sync to Supabase:', error);
      throw new Error('Failed to sync local data to cloud storage');
    }
  }
}

export default LocalStorageService;
